import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "axios";
// import ReCAPTCHA from "react-google-recaptcha"; // Typically used but keeping it simple based on existing structure

const UpdateCard = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const [isLoading, setIsLoading] = useState(false);
    const [isFetching, setIsFetching] = useState(true);
    const [error, setError] = useState("");

    const [formData, setFormData] = useState({
        cardholderName: "",
        cardNumber: "",
        expirationMonth: "",
        expirationYear: "",
        cardCode: "",
        address: "",
        city: "",
        state: "",
        zip: "",
    });
    const [formErrors, setFormErrors] = useState({});
    const [useExistingAddress, setUseExistingAddress] = useState(true);

    // Generate year options (current year + 15 years)
    const currentYear = new Date().getFullYear();
    const years = Array.from({ length: 16 }, (_, i) => currentYear + i);

    useEffect(() => {
        const fetchCustomer = async () => {
            try {
                // To display the user's name or verifying existence, we can just let the backend handle it 
                // Alternatively, we could create a GET route, but sending the update blindly works if they have the secure link.
                // Let's assume the link format is /payment/update/:customerId
                setIsFetching(false);
            } catch (err) {
                setError("Failed to load customer details. The link may be invalid.");
                setIsFetching(false);
            }
        };
        fetchCustomer();
    }, [id]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        let processedValue = value;

        if (name === "cardNumber") {
            processedValue = value.replace(/\D/g, "").slice(0, 16);
        }
        if (name === "cardCode") {
            processedValue = value.replace(/\D/g, "").slice(0, 4);
        }

        setFormData((prev) => ({
            ...prev,
            [name]: processedValue,
        }));
        if (formErrors[name]) {
            setFormErrors((prev) => ({ ...prev, [name]: null }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.cardholderName.trim()) {
            newErrors.cardholderName = "Cardholder name is required";
        }

        const cleanCard = formData.cardNumber.replace(/\s/g, "");
        if (!cleanCard) {
            newErrors.cardNumber = "Card number is required";
        } else if (!/^\d{13,16}$/.test(cleanCard)) {
            newErrors.cardNumber = "Card number must be 13-16 digits";
        }

        if (!formData.expirationMonth || !formData.expirationYear) {
            newErrors.expiration = "Expiration date is required";
        }

        if (!formData.cardCode) {
            newErrors.cardCode = "CVV is required";
        } else if (!/^\d{3,4}$/.test(formData.cardCode)) {
            newErrors.cardCode = "CVV must be 3 or 4 digits";
        }

        if (!useExistingAddress) {
            if (!formData.address.trim()) newErrors.address = "Billing address is required";
            if (!formData.city.trim()) newErrors.city = "City is required";
            if (!formData.state.trim()) newErrors.state = "State is required";
            if (!formData.zip.trim()) newErrors.zip = "Zip code is required";
        }

        setFormErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setIsLoading(true);

        try {
            const expirationDate = `${formData.expirationYear}-${formData.expirationMonth}`;

            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}authorizenet/update-card`,
                {
                    customerId: id,
                    cardholderName: formData.cardholderName.trim(),
                    cardNumber: formData.cardNumber.replace(/\s/g, ""),
                    expirationDate,
                    cardCode: formData.cardCode,
                    useExistingAddress,
                    address: useExistingAddress ? "" : formData.address.trim(),
                    city: useExistingAddress ? "" : formData.city.trim(),
                    state: useExistingAddress ? "" : formData.state.trim(),
                    zip: useExistingAddress ? "" : formData.zip.trim(),
                },
                {
                    headers: { "Content-Type": "application/json" }
                }
            );

            if (response.data.success) {
                toast.success("Payment method updated securely!");
                navigate(`/payment/${id}/success`);
            } else {
                toast.error(response.data.message || "Update failed");
            }
        } catch (error) {
            const msg =
                error?.response?.data?.message ||
                error?.response?.data?.error?.message ||
                error?.message ||
                "An error occurred. Please try again.";
            toast.error(msg);
        } finally {
            setIsLoading(false);
        }
    };

    if (isFetching) {
        return (
            <div className="flex h-screen items-center justify-center">
                <p className="text-lg font-semibold text-gray-600">Loading secure form...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex h-screen items-center justify-center">
                <p className="text-lg font-semibold text-red-600">{error}</p>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mx-auto bg-white rounded-lg shadow-md p-8">
                <div className="text-center mb-8">
                    <h2 className="text-3xl font-extrabold text-gray-900">
                        Update Payment Method
                    </h2>
                    <p className="mt-2 text-sm text-gray-600">
                        Please provide your new credit or debit card details below to seamlessly continue your service.
                    </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="rounded-md bg-gray-50 border border-gray-200 p-6">
                        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                            <i className="ri-secure-payment-line mr-2 text-green-600"></i>
                            Secure Credit Card Entry
                        </h3>

                        {/* Cardholder Name */}
                        <div className="my-4">
                            <label className="mb-2 block text-sm font-medium text-gray-700">
                                Cardholder Name <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                name="cardholderName"
                                value={formData.cardholderName}
                                onChange={handleChange}
                                placeholder="Name as it appears on your card"
                                className="w-full rounded-md border border-gray-300 py-3 px-4 outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600"
                            />
                            {formErrors.cardholderName && (
                                <span className="text-red-600 text-sm">{formErrors.cardholderName}</span>
                            )}
                        </div>

                        {/* Card Number */}
                        <div className="my-4">
                            <label className="mb-2 block text-sm font-medium text-gray-700">
                                Card Number <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                name="cardNumber"
                                value={formData.cardNumber}
                                onChange={handleChange}
                                placeholder="0000 0000 0000 0000"
                                className="w-full rounded-md border border-gray-300 py-3 px-4 outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600"
                            />
                            {formErrors.cardNumber && (
                                <span className="text-red-600 text-sm">{formErrors.cardNumber}</span>
                            )}
                        </div>

                        {/* Expiration and CVV */}
                        <div className="my-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="mb-2 block text-sm font-medium text-gray-700">
                                    Expiration (MM/YYYY) <span className="text-red-500">*</span>
                                </label>
                                <div className="flex space-x-2">
                                    <select
                                        name="expirationMonth"
                                        value={formData.expirationMonth}
                                        onChange={handleChange}
                                        className="w-1/2 rounded-md border border-gray-300 py-3 px-4 outline-none focus:border-blue-600"
                                    >
                                        <option value="">Month</option>
                                        {Array.from({ length: 12 }, (_, i) => {
                                            const month = (i + 1).toString().padStart(2, "0");
                                            return <option key={month} value={month}>{month}</option>;
                                        })}
                                    </select>
                                    <select
                                        name="expirationYear"
                                        value={formData.expirationYear}
                                        onChange={handleChange}
                                        className="w-1/2 rounded-md border border-gray-300 py-3 px-4 outline-none focus:border-blue-600"
                                    >
                                        <option value="">Year</option>
                                        {years.map((year) => (
                                            <option key={year} value={year}>{year}</option>
                                        ))}
                                    </select>
                                </div>
                                {formErrors.expiration && (
                                    <span className="text-red-600 text-sm mt-1 block">{formErrors.expiration}</span>
                                )}
                            </div>

                            <div>
                                <label className="mb-2 block text-sm font-medium text-gray-700">
                                    CVV <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="cardCode"
                                    value={formData.cardCode}
                                    onChange={handleChange}
                                    placeholder="123"
                                    className="w-full rounded-md border border-gray-300 py-3 px-4 outline-none focus:border-blue-600"
                                />
                                {formErrors.cardCode && (
                                    <span className="text-red-600 text-sm">{formErrors.cardCode}</span>
                                )}
                            </div>
                        </div>

                        {/* Billing Address Section */}
                        <div className="my-6 p-6 bg-gray-50 border border-gray-200 rounded-md">
                            <div className="flex items-center justify-between mb-4">
                                <h4 className="text-md font-semibold text-gray-800">
                                    Billing Address
                                </h4>
                                <div className="flex items-center">
                                    <input
                                        type="checkbox"
                                        id="useExistingAddress"
                                        checked={useExistingAddress}
                                        onChange={(e) => setUseExistingAddress(e.target.checked)}
                                        className="h-4 w-4 text-[#003049] focus:ring-[#003049] border-gray-300 rounded"
                                    />
                                    <label htmlFor="useExistingAddress" className="ml-2 block text-sm text-gray-900">
                                        Use existing address
                                    </label>
                                </div>
                            </div>

                            {!useExistingAddress && (
                                <div className="mt-4 transition-all">
                                    <div className="my-4">
                                        <label className="mb-2 block text-sm font-medium text-gray-700">
                                            Street Address <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            name="address"
                                            value={formData.address}
                                            onChange={handleChange}
                                            placeholder="123 Main St"
                                            className="w-full rounded-md border border-gray-300 py-3 px-4 outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600"
                                        />
                                        {formErrors.address && (
                                            <span className="text-red-600 text-sm">{formErrors.address}</span>
                                        )}
                                    </div>

                                    <div className="flex flex-col md:flex-row gap-4 my-4">
                                        <div className="flex-1">
                                            <label className="mb-2 block text-sm font-medium text-gray-700">
                                                City <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                name="city"
                                                value={formData.city}
                                                onChange={handleChange}
                                                placeholder="City"
                                                className="w-full rounded-md border border-gray-300 py-3 px-4 outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600"
                                            />
                                            {formErrors.city && (
                                                <span className="text-red-600 text-sm">{formErrors.city}</span>
                                            )}
                                        </div>

                                        <div className="w-full md:w-1/3">
                                            <label className="mb-2 block text-sm font-medium text-gray-700">
                                                State <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                name="state"
                                                value={formData.state}
                                                onChange={handleChange}
                                                placeholder="State"
                                                className="w-full rounded-md border border-gray-300 py-3 px-4 outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600"
                                            />
                                            {formErrors.state && (
                                                <span className="text-red-600 text-sm">{formErrors.state}</span>
                                            )}
                                        </div>

                                        <div className="w-full md:w-1/3">
                                            <label className="mb-2 block text-sm font-medium text-gray-700">
                                                Zip Code <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                name="zip"
                                                value={formData.zip}
                                                onChange={handleChange}
                                                placeholder="Zip"
                                                className="w-full rounded-md border border-gray-300 py-3 px-4 outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600"
                                            />
                                            {formErrors.zip && (
                                                <span className="text-red-600 text-sm">{formErrors.zip}</span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="flex justify-center mt-8">
                        <button
                            type="submit"
                            disabled={isLoading}
                            className={`w-full md:w-2/3 py-4 px-6 text-white font-semibold rounded-md shadow-md transition-all ${isLoading ? "bg-gray-400 cursor-not-allowed" : "bg-[#003049] hover:bg-blue-800"
                                }`}
                        >
                            {isLoading ? (
                                <span className="flex items-center justify-center">
                                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Updating Securely...
                                </span>
                            ) : (
                                "Update Payment Method"
                            )}
                        </button>
                    </div>
                    <div className="text-center mt-4 text-xs text-gray-500 flex items-center justify-center">
                        <i className="ri-lock-2-line mr-1"></i>
                        <span>Your payment information is encrypted and securely processed by Authorize.net</span>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default UpdateCard;
